/**
 * Document highlight — every occurrence of the symbol at the cursor WITHIN the
 * current document (declaration = Write, use-sites = Read). Backed by the same
 * ReferenceIndex + CursorContext as find-references, so it correctly covers
 * v0.8.2 keyword-named, unicode, and hoisted references (which the client's
 * textual fallback highlighter gets wrong).
 */
import { DocumentHighlightKind, type DocumentHighlight } from "vscode-languageserver";
import type { CursorContext } from "../workspace/cursor-context.js";
import type { ReferenceIndex } from "../workspace/reference-index.js";
import type { SymbolTable } from "../workspace/symbol-table.js";
import type { Workspace } from "../workspace/workspace.js";
import { resolveSymbolAtCursor } from "../workspace/resolve.js";
import { toLspRange } from "../lsp-types.js";

export function provideDocumentHighlight(
  workspace: Workspace,
  symbols: SymbolTable,
  cursor: CursorContext,
  refs: ReferenceIndex,
  uri: string,
  line: number,
  character: number
): DocumentHighlight[] {
  const sym = resolveSymbolAtCursor(workspace, symbols, cursor, uri, line, character);
  if (!sym) return [];

  const out: DocumentHighlight[] = [];
  const declSpan = sym.nameSpan ?? sym.declSpan;
  if (sym.definingUri === uri && declSpan) {
    out.push({ range: toLspRange(declSpan), kind: DocumentHighlightKind.Write });
  }
  for (const r of refs.referencesTo(sym.id)) {
    if (r.uri === uri) out.push({ range: toLspRange(r.span), kind: DocumentHighlightKind.Read });
  }
  return out;
}
